import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { TImage, TImageToLoad } from '@components/typescript';
import styles from '@components/Slider/Slider.styles';
import { useResponsive } from '@features/responsive';
import logger from '@utils/logger';
export type PropsType = {
  canvasImages: TImage[];
};
const CANVAS_ASPECT_RATIO = 1920 / 900;
const SliderCanvasAnimation = ({
  canvasImages
}: PropsType) => {
  const [canvasWidth, setCanvasWidth] = useState(1920);
  const [canvasHeight, setCanvasHeight] = useState(900);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const requestRef = useRef<number | null>(null);
  const {
    isWiderThanMobilelg
  } = useResponsive();
  const [xOffset, setXOffset] = useState(0);
  const [images] = useState<TImageToLoad[]>(canvasImages && canvasImages.map(image => ({
    loaded: false,
    src: image
  })));
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [imagesReady, setImagesReady] = useState(false);
  let index = 0;
  useEffect(() => {
    if (!images) return;
    images.forEach(img => {
      if (!img.src.desktop) return;
      const newImg = new window.Image();
      newImg.src = typeof img.src.desktop === 'string' ? img.src.desktop : img.src.desktop?.path;
      newImg.onload = () => {
        img.loaded = true;
        imagesRef.current.push(newImg);
      };
      newImg.onerror = () => logger.error('image loading error');
    });
    setImagesReady(images.every(img => img.loaded));
  }, [images]);
  const draw = (x: number, y: number) => {
    const img = imagesRef.current[index];
    if (img && ctx) ctx.drawImage(img, x, y, canvasWidth, canvasHeight);
  };
  const update = () => {
    index = index === images.length ? 1 : index + 1;
    draw(xOffset, 0);
  };
  const animate = () => {
    requestRef.current = requestAnimationFrame(animate);
    update();
  };
  useEffect(() => {
    if (canvasRef.current) {
      setCtx(canvasRef.current.getContext('2d'));
    }
  }, [imagesReady]);
  useEffect(() => {
    if (!imagesReady) return () => null;
    if (ctx) animate();
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [ctx, canvasWidth, canvasHeight, xOffset]);
  const resizeCanvas = () => {
    if (!canvasRef.current) return;

    // biome-ignore lint/correctness/noUnsafeOptionalChaining: intentional
    const {
      clientWidth,
      clientHeight
    } = canvasRef?.current;
    canvasRef.current.width = clientWidth;
    canvasRef.current.height = clientHeight;
    let newWidth = canvasRef.current.width;
    let newHeight = canvasRef.current.width / CANVAS_ASPECT_RATIO;
    if (newHeight < canvasRef.current.height) {
      newHeight = canvasRef.current.height;
      newWidth = newHeight * CANVAS_ASPECT_RATIO;
    }
    setXOffset(newWidth > canvasRef.current.width ? (canvasRef.current.width - newWidth) / 2 : 0);
    setCanvasWidth(newWidth);
    setCanvasHeight(newHeight);
  };
  useEffect(() => {
    setTimeout(resizeCanvas, 1000);
  }, [isWiderThanMobilelg]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      resizeCanvas();
      window.addEventListener('resize', debounce(resizeCanvas, 500));
    }
  }, []);
  useEffect(() => {
    if (canvasRef.current) canvasRef.current.width = canvasRef.current.clientWidth;
  }, [canvasRef?.current?.clientWidth]);
  return <div sx={styles.sliderContainer(false)}>
      <canvas sx={styles.canvas} ref={canvasRef} width={canvasWidth} height={canvasHeight} />
    </div>;
};
export default SliderCanvasAnimation;